import React, {useState, useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {Alert, Button, Badge, Container} from 'reactstrap'
import compose from 'lodash/flowRight'
import useSmartNavigate from 'hooks/useSmartNavigate'
import useUTCTimeout from 'hooks/useUTCTimeout'
import useForceRender from 'hooks/useForceRender'
import useDebug from 'hooks/useDebug'
import {useInterval} from "usehooks-ts";

import ListWithSort from 'components/blocks/ListWithSort'
import Block from 'components/fieldBlocks/Block'
import MarkdownVal from 'components/fields/MarkdownVal'
import UTCTimerSpan from 'components/elements/UTCTimerSpan'
import Link from 'components/links/Link'
import LinkInline from 'components/links/LinkInline'
import withInitialDataLoadWaiting from 'hocs/withInitialDataLoadWaiting'
import withUriParams from 'hocs/withUriParams'
import SolutionsView, {getScenarioSolutionsStats} from './SolutionsView'
import SaveButtonsBlock from 'components/blocks/SaveButtonsBlock'

import {queryScenario, querySolutions} from '../data/eventQueries'
import {createSolution, deleteSolution, cloneSolution, registerInEvent, deleteScenarioScenarioUserCustomization} from '../data/eventMutations'

import {convertOldJSONPrototypeParameters, convertOldJSONPrototypeResults} from 'appEvent/data/eventObjects'
import {breadcrumbsStore} from 'appBase/TopNav'
import entityUtils from 'utils/entityUtils'
import dateUtils, {MAX_DATE} from 'utils/dateUtils'
import {parseBackendMessage} from 'utils/translationUtils'
import {SCENARIO} from 'constants/constants'
import {useMatch} from '@reach/router'


const ScenarioView = compose(
    withUriParams([
        ['solutionsMode', String]]),
    queryScenario,
    //pollSolutions,
    querySolutions,
    createSolution,
    deleteSolution,
    cloneSolution,
    registerInEvent,
    deleteScenarioScenarioUserCustomization,
    withInitialDataLoadWaiting(['scenario']) //, 'solutions'])
)(props => {
    useDebug("ScenarioView", props);

    const {
        solutionsMode, user, event, solutions, additionalEventStatus, additionalButtonsBlockInfo,
        createSolution, deleteSolution, cloneSolution, registerInEvent, deleteScenarioScenarioUserCustomization,
        loading,
        // features:
        enableBackLink, showBackLink, prevNextLinkState, hasFewVersions } = props;

    const uriMatch = useMatch('/:lang/:region/*');
    const navigate = useSmartNavigate();
    const forceRender = useForceRender();
    const {t, i18n} = useTranslation();
    const _t = (value, obj) => t('appEvent.views.ScenarioView.' + value, obj);

    const [error, setError] = useState(null);

    // parsing JSON

    const scenario = useMemo(() => (
        props.scenario && {
            ...props.scenario,
            prototypeParameters: entityUtils.safeJSONParse(convertOldJSONPrototypeParameters(props.scenario.prototypeParameters)),
            prototypeResults: entityUtils.safeJSONParse(convertOldJSONPrototypeResults(props.scenario.prototypeResults)),
            parameters: entityUtils.safeJSONParse(props.scenario.parameters)
        }), [props.scenario]);

    // preparing links

    const containerId = SCENARIO;
    breadcrumbsStore.register(containerId);
    const breadcrumbs = [];
    scenario && breadcrumbs.push({
        name: scenario.name,
        to: props.uri,
        back: enableBackLink && 1,
        nonBlocking: true
    });

    useEffect(() => {
        const isTerminal = !solutionsMode;
        breadcrumbsStore.set(containerId, breadcrumbs, isTerminal);
    });

    // force render if time

    const UTCDateTimestamp = dateUtils.getCurrentServerUTCDate().getTime();
    const eventEndsAtUTCTimestamp = Date.parse(event.endsAt) || MAX_DATE;       // MAX_DATE if undefined
    const scenarioEndsAtUTCTimestamp = (scenario && Date.parse(scenario.endsAt)) || MAX_DATE; // MAX_DATE if undefined
    const endsAtUTCTimestamp = dateUtils.getMinOfExistingDates(eventEndsAtUTCTimestamp, scenarioEndsAtUTCTimestamp);
    const isEnded = endsAtUTCTimestamp <= UTCDateTimestamp;
    const disabled = props.disabled || isEnded;
    useUTCTimeout(
        forceRender,
        !isEnded ? endsAtUTCTimestamp : null
    );

    const isScenarioCustomizationRunning =
        scenario?.scenarioUserCustomizationStatus &&
        !['finished', 'failed'].includes(scenario.scenarioUserCustomizationStatus) ;
    const isScenarioCustomizationFailed =
        scenario?.scenarioUserCustomizationStatus &&
        ['failed'].includes(scenario.scenarioUserCustomizationStatus) ;

    useInterval(() => {
        props.refetch.scenario();
    }, isScenarioCustomizationRunning ? 1000 : null);

    // subnavigation

    if (!scenario ||                                        // no scenario was loaded (or not authorized)
        scenario.eventId !== event.id ||                    // not matching the event
        (solutionsMode && solutionsMode !== 'solutions'))     // wrong internal location
    {
        navigate(`${props.uri}/..`);
        return null;
    }


    // rendering

    const sections = [];

    if (scenario.description && scenario.description.trim()) {
        sections.push({
            className: "description-tab-icon",
            name: _t('description'),
            title: _t('description'),
            key: "description"
        });
    }
    if (scenario.descriptionParameters && scenario.descriptionParameters.trim()) {
        sections.push({
            className: "init-data-tab-icon",
            name: _t('descriptionParameters'),
            title: _t('descriptionParameters'),
            key: "descriptionParameters"
        });
    }
    if (scenario.descriptionScore && scenario.descriptionScore.trim()) {
        sections.push({
            className: "criteria-tab-icon",
            name: _t('descriptionScore'),
            title: _t('descriptionScore'),
            key: "descriptionScore"
        });
    }
    sections.push({
        className: "solutions-tab-icon",
        name: _t(scenario.type !== 'form' ? 'solutions': 'solutionsForm'),
        title: _t(scenario.type !== 'form' ? 'solutions': 'solutionsForm'),
        key: "solutions"
    });

    if (isScenarioCustomizationRunning || isScenarioCustomizationFailed) {
        ['description', 'descriptionParameters', 'descriptionScore'].forEach(descriptionField =>
            scenario[descriptionField] = scenario[descriptionField] && (
                isScenarioCustomizationRunning
                    ? _t('customizationIsRunning')
                    : _t('customizationFailed')));
    }


    const headers = ["#", _t('creationDate'), _t('sendDate'), _t('student'), _t('state'), _t('comment')];
    const sizes = [80, 250, 150, 150, "1*"];
    if (scenario.type !== 'form') {
        headers.push(_t('grade'));
        sizes.push(200)
    }

    const {maxSolutionsNumber, isMaxSentSolutionsNumberReached} =
        getScenarioSolutionsStats(scenario, solutions);

    let rows = [];
    if (loading.solutions) {
        rows = [{fields: [_t('loading')], notSelectable: true}];
    }
    if (solutions) {
        rows = solutions.map((s) => {
            let state = "";
            switch (s.status) {
                case "ready":
                    state = _t('ready');
                    break;
                case "running":
                    // TODO: сделать: 1. менять текст только для generative_test, 2. заменить строки перевода на runningCalculation и runningScoring
                    state = ["test", "generative_test", "prog"].includes(scenario.type) ?
                        _t('running2'):
                        _t('running');
                    break;
                case "evaluating":
                    state = _t('evaluating');
                    break;
                case "finished":
                    state = _t(scenario.type !== 'form' ? 'finished': 'finishedForm');
                    break;
                default:
                    state = _t('error');
            }

            const row = {
                fields: [<span className={"sim-" + s.status + "-name"}>{s.number}</span>,
                    <span>{dateUtils.dateToLocaleDateTimeString(s.createdAt, i18n)}</span>,
                    <span>{dateUtils.dateToLocaleDateTimeString(s.modifiedAt, i18n)}</span>,
                    <span>{s.user.type === 'user'
                        ? s.user.name
                        : "(" + t('appEvent.views.ScenarioView.guestUser') + ")"}
                    </span>,
                    <span>{state}</span>, <span>{s.description}</span>],
                actions: {
                    "select": () => navigate(`${props.uri}/solutions/${s.id}`)
                }
            };

            if (scenario.type !== 'form') {
                row.fields.push(<span>{s.score && s.score + _t('points')} </span>);
            }

            if (!disabled) {
                row.actions.clone = !isMaxSentSolutionsNumberReached && (() => cloneSolution(s.id)
                    .then(({data: {cloneSolution: {solution, error}}}) => {
                        setError(error ? parseBackendMessage(error, t): null);
                        if (!error) {
                            navigate(`${props.uri}/solutions/${solution.id}`);
                        }
                    }));

                if (s.status === "ready" && s.user.id === user.id) {
                    row.actions.removeConfirmation = () => deleteSolution(s.id)
                        .then(({data: {deleteSolution: {error}}}) => setError(error ? parseBackendMessage(error, t): null));
                }
            }
            return row;
        });
    }


    const registeredInEvent = event && (event.isStudent || event.isTester);
    if (!disabled && event) {
        // registered in event
        if (registeredInEvent) {
            // can create solutions
            if (solutions && !isMaxSentSolutionsNumberReached && !(isScenarioCustomizationRunning || isScenarioCustomizationFailed)) {
                rows.push({
                    fields: [scenario.type === 'form' ? _t('createSolutionForm'): _t('createSolution')],
                    className: "text-end",
                    notSelectable: true,
                    actions: {
                        "click": () => createSolution({
                            scenarioId: scenario.id,
                            description: ""
                        }).then(({data: {createSolution: {solution, error}}}) => {
                            setError(error ? parseBackendMessage(error, t): null);
                            if (!error) {
                                navigate(`${props.uri}/solutions/${solution.id}`);
                            }
                        })
                    }
                });
            }
            // guest
            if (user.type === 'guest') {
                rows.push({
                    fields: [
                        <span className="text-wrap">
                            {_t(scenario.type !== 'form' ? 'guestCanSubmitMain' : 'guestCanSubmitMainForm')}
                            &nbsp;
                            <LinkInline to={`/${uriMatch.lang}/${uriMatch.region}/auth/login`}
                                  state={{goBackLinkData: {to: `${props.uri}${props.location.search}`}}}>
                                {_t('guestCanSubmitLink')}
                            </LinkInline>
                        </span>],
                    className: "text-end m-3",
                    notSelectable: true
                });
            }
            if (scenario.type !=='form' && isMaxSentSolutionsNumberReached) {
                rows.push({
                    fields: [_t(maxSolutionsNumber === 1 ? 'uniqueSolution': 'maxSolutionsSent')],
                    className: "text-end",
                    notSelectable: true
                });
            } else if (scenario.type ==='form' && isMaxSentSolutionsNumberReached) {
                rows.push({
                    fields: [_t(maxSolutionsNumber === 1 ? 'uniqueSolutionForm': 'maxSolutionsSentForm')],
                    className: "text-end",
                    notSelectable: true
                });
            }
        }
        // not registered in event
        if (!registeredInEvent) {
            // self-registration is not available
            if (!event.selfRegistrationType) {
                rows.push({
                    fields: [_t(scenario.type !== 'form' ? 'registrationIsNotAllowed': 'registrationIsNotAllowedForm')],
                    className: "text-end",
                    notSelectable: true
                });
            }
            // self-registration is avaiable
            if (event.selfRegistrationType) {
                // guest
                if (user.type === 'guest' && event.selfRegistrationType === 'user') {
                    rows.push({
                        fields: [_t(scenario.type !== 'form' ? 'notAuthorised': 'notAuthorisedForm')],
                        className: "text-end",
                        notSelectable: true,
                        actions: {
                            "click": () => navigate(
                                `/${uriMatch.lang}/${uriMatch.region}/auth/login`,
                                {state: {goBackLinkData: {to: `/${uriMatch.lang}/${uriMatch.region}/events/${event.license.id}/${event.id}/${scenario.id}`}}})
                        }
                    });
                }
                // registered user
                if (user.type === 'user') {
                    rows.push({
                        fields: [_t(scenario.type !== 'form' ? 'notJoined': 'notJoinedForm')],
                        className: "text-end",
                        notSelectable: true,
                        actions: {
                            "click": () =>
                                registerInEvent(event.id)
                                    .then(({data: {registerInEvent: {result, error}}}) => {
                                        setError(error ? parseBackendMessage(error, t) : null);
                                    })
                        }
                    });
                }
            }
        }
    }

    // previous & next scenario
    const createScenarioUrlString = (id) => {
        const urlParts = props.uri.split('/');
        urlParts[urlParts.length - 1] = id;
        return urlParts.join('/');
    };

    const currentIndex = event.scenarios.findIndex(s => s.id === scenario.id);
    const childScenarios = event.scenarios.filter(s => s.parentId === scenario.id)
                                          .map((s, i) =>
        (<Link key={i} to={createScenarioUrlString(s.id)}>
            <div className="link-primary nav-link pointer d-block">
                {s.name}
            </div>
         </Link>));

    const isFirstScenario = currentIndex === 0;
    const isLastScenario = currentIndex === event.scenarios.length - 1;

    const prevScenario = currentIndex ?
        event.scenarios[currentIndex - 1]:
        undefined;
    const nextScenario = currentIndex !== event.scenarios.length - 1 ?
        event.scenarios[currentIndex + 1]:
        undefined;

    const prevNextLinks = (
        <div className="d-flex justify-content-between mb-3">
            {!isFirstScenario &&
                <Link to={createScenarioUrlString(prevScenario.id)}>
                    <div className={`nav-button left`}>
                        {_t('back')}
                    </div>
                </Link>}

            {!isLastScenario &&
                <div className={'ms-auto'}>
                    <Link to={createScenarioUrlString(nextScenario.id)}>
                        <div className={`nav-button right`}>
                            {_t('forward')}
                        </div>
                    </Link>
                </div>}
        </div>
    );

    return (
        <>
        {error &&
        <Alert className="mb-0" color="danger">{error}</Alert>}

        {!solutionsMode &&
        <div className="flex-grow d-flex flex-column scroll-parent">
            {showBackLink &&
            <SaveButtonsBlock withBack
                              additionalInfo={additionalButtonsBlockInfo}
                              executeWithLock={props.executeWithLock} />}

            {scenario.type !== 'page' &&
            <Container fluid="lg">
            <div className="flex-grow d-flex flex-column scroll">
                <div className="p-3 d-block d-lg-flex text-dark">
                    <div>
                        <h5>{scenario.name}{additionalEventStatus}</h5>
                        <div>{event.name}</div>
                        {scenario.rating &&
                        <div>{_t('rating')} {scenario.rating} {_t('from')} {scenario.ratingMax}</div>}
                        {!isEnded && endsAtUTCTimestamp < MAX_DATE &&
                        <div>{_t('timeLeft')}<UTCTimerSpan endsAtUTC={endsAtUTCTimestamp} /></div>}
                        {isEnded &&
                        <div className="text-danger">{_t('isEnded')}</div>}
                    </div>

                    {scenario.imageUrl &&
                    <div>
                        <img className="big-imgs-choice-pic" src={scenario.imageUrl} alt={scenario.name}/>
                    </div>}
                </div>

                {/* BlockFields propagate data to children */}
                {scenario.type !=='form' &&
                    <Block sections={sections}>
                        {['description', 'descriptionParameters', 'descriptionScore']
                            .filter((field) => scenario[field])
                            .map((field) => (
                                <MarkdownVal key={field} section={field} value={scenario[field]} />))}

                        {scenario.description && scenario.type === 'generative_test' &&
                            (event.isAdmin || event.isTester || scenario.scenarioUserCustomizationStatus === 'failed') &&
                            <div section="description">
                               {scenario.scenarioUserCustomizationLog &&
                                    <>
                                       <h6>{t('appAdmin.views.LicenseScenarioView.scenarioUserCustomizationLog')}</h6>
                                       <div>{scenario.scenarioUserCustomizationLog}</div>
                                    </>}

                                <Button color="danger" className="my-3" disabled={disabled}
                                        onClick={() => {
                                            deleteScenarioScenarioUserCustomization(scenario.id)
                                                .then(({data: {deleteScenarioScenarioUserCustomization: {id, error}}}) =>
                                                    setError(error ? parseBackendMessage(error, t) : null))
                                        }}>
                                    {_t('resetScenarioUserCustomizationProgram')}
                                </Button>
                            </div>}

                        <Block section="solutions">
                            {event.isTester &&
                                <div><Badge color="secondary">{_t('testMode')}</Badge></div>}
                            <ListWithSort headers={headers} sizes={sizes} rows={rows} />
                        </Block>
                    </Block>}

            {scenario.type ==='form' &&
                <div>
                    {scenario.description &&
                        <div>
                            <h6>{_t('description')}</h6>
                            <MarkdownVal value={scenario.description} />
                        </div>}

                    <br/>

                    <h6>{_t('solutionsForm')}</h6>
                    {event.isTester &&
                        <div><Badge color="secondary">{_t('testMode')}</Badge></div>}
                    <ListWithSort headers={headers} sizes={sizes} rows={rows} />
                </div>}

            </div>
            </Container>}

            {scenario.type === 'page' &&
            <div className="flex-grow workpanel-gray bg-white scroll">
                <Container fluid="lg">
                    {(isFirstScenario && hasFewVersions) && (<div className="alert alert-warning" role="alert">
                        {_t('fewVersions')}
                    </div>)}

                    {prevNextLinkState &&
                        prevNextLinks}

                    {scenario.description &&
                        <MarkdownVal value={scenario.description}/>}

                    {childScenarios &&
                        childScenarios}

                    {prevNextLinkState &&
                        prevNextLinks}
                </Container>
            </div>}
        </div>}

        {solutionsMode === 'solutions' && scenario.type !== 'page' &&
        <SolutionsView event={event} scenario={scenario} additionalEventStatus={additionalEventStatus}
                       scenarioId={scenario.id} // for querySolutions
                       disabled={disabled}   // to turn on read-only mode
                       enableBackLink   // always fullscreen inside scenario
                       showBackLink={showBackLink}
                       isEnded={isEnded}    // to display info message about ending
                       additionalButtonsBlockInfo={additionalButtonsBlockInfo}
                       user={props.user} loading={props.loading} errors={props.errors}  // this will stop SolutionsView.pollSolutions from running
                       solutions={solutions}    // this will stop SolutionsView.querySolutions from running
                       setActionLockWithMessageAndActions={props.setActionLockWithMessageAndActions}
                       resetActionLock={props.resetActionLock}
                       executeWithLock={props.executeWithLock}
                       executeWithConfirmation={props.executeWithConfirmation}
                       uri={`${props.uri}/solutions`} uriParams={props.uriParams} location={props.location} />}
        </>);
});

export default ScenarioView;